import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, { tokenStore } from '@/lib/api';
import { logger } from '@/lib/logger';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { useSessionExpiryWatcher } from '@/lib/useSessionExpiryWatcher';
import { closeAllLeaderboardConnections } from '@/lib/leaderboardWSPool';

const PlayerAuthContext = createContext(null);

export const usePlayerAuth = () => {
  const ctx = useContext(PlayerAuthContext);
  if (!ctx) throw new Error('usePlayerAuth must be used within PlayerAuthProvider');
  return ctx;
};

// Sent on login/signup so deadline emails quote the player's own local time —
// pools regularly have members in more than one country.
const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

export const PlayerAuthProvider = ({ children }) => {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlayer = useCallback(async () => {
    try {
      const { data } = await api.get('/player/me');
      setPlayer(data);
    } catch {
      setPlayer(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPlayer(); }, [loadPlayer]);

  // Mirror player identity into Sentry so captured errors are tied to
  // the player who hit them. No-op when Sentry isn't configured.
  useEffect(() => {
    if (player) setSentryUser(player); else clearSentryUser();
  }, [player]);

  const signup = async (email, password, firstName, lastName, captchaToken) => {
    const { data } = await api.post('/player/auth/signup', {
      email, password, first_name: firstName, last_name: lastName,
      cf_turnstile_response: captchaToken, timezone: browserTimezone()
    });
    if (!data.requires_email_confirmation) {
      if (data.token) tokenStore.setPlayer(data.token);
      setPlayer(data.player);
    }
    return data;
  };

  const login = async (email, password, captchaToken) => {
    const { data } = await api.post('/player/auth/login', {
      email, password, cf_turnstile_response: captchaToken, timezone: browserTimezone()
    });
    if (data.token) tokenStore.setPlayer(data.token);
    setPlayer(data.player);
    return data;
  };

  const logout = async () => {
    try { await api.post('/player/auth/logout'); } catch (err) { logger.warn('Logout request failed:', err.message); }
    tokenStore.clearPlayer();
    api.clearCache?.();
    closeAllLeaderboardConnections();
    setPlayer(null);
    localStorage.removeItem('fp_player_name');
    localStorage.removeItem('fp_entry_id');
    // Caller is expected to navigate via React Router (smooth client-side route change).
  };

  const isAuthenticated = !!player;

  // Auto-logout when the player's JWT is about to hit its `exp` (8h
  // server-side hard cap). Mirrors the admin AuthContext behaviour so
  // both audiences get the same friendly "session ended" toast instead
  // of a silent 401 on the next request.
  const onSessionExpire = useCallback(() => {
    logger.info('Player session expired - auto-logging out.');
    tokenStore.clearPlayer();
    api.clearCache?.();
    closeAllLeaderboardConnections();
    setPlayer(null);
    localStorage.removeItem('fp_player_name');
    localStorage.removeItem('fp_entry_id');
    if (typeof window !== 'undefined') window.location.href = '/login';
  }, []);

  useSessionExpiryWatcher({
    token: player ? tokenStore.getPlayer() : null,
    enabled: !!player,
    onExpire: onSessionExpire,
    message: 'Your session has ended after 8 hours. Please sign in again.',
  });

  const value = useMemo(() => ({
    player, setPlayer, loading,
    signup, login, logout, loadPlayer,
    isAuthenticated
  }), [player, loading, loadPlayer, isAuthenticated]);

  return (
    <PlayerAuthContext.Provider value={value}>
      {children}
    </PlayerAuthContext.Provider>
  );
};
